import {sortBy} from 'lodash';

import {isAnchoredInLang, isTextAnnotation, resolveAnchor} from '../engine/annotationAnchors';
import {scrollerContentOffset} from '../engine/annotationIndicatorPlacement';
import type {TextProjection} from '../engine/annotationTextProjection';
import {normalizedRangeToDomRange} from '../engine/annotationTextProjection';
import type {Annotation, AnnotationRange, TextAnnotation} from '../types';

/** The `xs` indicator button's height — closer centers cannot render apart. */
const INDICATOR_SLOT_PX = 24;

/** The region's first character, which sits on its first text line. */
const REGION_START: AnnotationRange = {start: 0, end: 1};

export interface AnnotationNoteLine {
  /** The line's vertical center within the scroller's scrolled content. */
  center: number;
  /** The noted marks on this line, in the order they arrived. */
  annotations: Annotation[];
}

export interface MeasuredNote {
  annotation: TextAnnotation;
  center: number;
}

/**
 * Where each noted mark's first line box sits in the scroller's scrolled content,
 * in document order. A mark made in a language other than the region's `lang`
 * that does not resolve there sits on the region's first text line, so its note
 * stays reachable. Any other mark whose anchor no longer resolves is dropped, as
 * the paint drops it; whole-supplement marks surface on the supplement instead.
 */
export function measureNotes(
  annotations: Annotation[],
  projection: TextProjection,
  scroller: HTMLElement,
  lang: string | null
): MeasuredNote[] {
  const contentOffset = scrollerContentOffset(scroller);
  const placed = annotations.filter(isTextAnnotation).flatMap((annotation) => {
    const resolved =
      resolveAnchor(annotation.anchor, projection) ??
      (isAnchoredInLang(annotation.anchor, lang) ? null : REGION_START);
    return resolved ? [{annotation, resolved}] : [];
  });
  return sortBy(placed, ({resolved}) => resolved.start).flatMap(({annotation, resolved}) => {
    const firstLine = normalizedRangeToDomRange(projection, resolved)?.getClientRects()[0];
    if (!firstLine) {
      return [];
    }
    return [{annotation, center: firstLine.top + firstLine.height / 2 + contentOffset}];
  });
}

/**
 * One entry per indicator that fits, each keeping input order and its first
 * note's center. Regions don't matter: a table row's cells share a slot.
 */
export function groupNotesByLine(notes: MeasuredNote[]): AnnotationNoteLine[] {
  const lines: AnnotationNoteLine[] = [];
  notes.forEach(({annotation, center}) => {
    const line = lines.find((candidate) => Math.abs(candidate.center - center) < INDICATOR_SLOT_PX);
    if (line) {
      line.annotations.push(annotation);
      return;
    }
    lines.push({center, annotations: [annotation]});
  });
  return lines;
}
